import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { getServiceDetails } from "../API/LinkedServicesApi";
import AuthContext from "../Context/AuthProvider";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fab,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  CloseOutlined,
  CreateOutlined,
  Visibility,
  NoteAdd,
  FileDownload,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  getPrescriptionsForService,
  removePrescription,
} from "../API/PrescriptionApi";
import {
  addclinicalNote,
  deleteClinicalNote,
  getClinicalNote,
  getClinicalNotesForService,
} from "../API/ClinicalNoteApi";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../Utility/UserMessageContext";

import LoadingScreen from "../Utility/LoadingScreen";
import { uploadMedicalRecord } from "../API/MedicalRecordApi";
import { formatAge } from "../Utility/Utility";
import {
  CardActions,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { iconButtonStyles, PrescriptionTypes } from "../Utility/Constants";
import {
  getMedicalRecordMetadata,
  downloadMedicalRecord,
} from "../API/MedicalRecordApi";
import { capitalizeFirstLetter } from "../Utility/Utility";
import {
  convertUTCToTimeZone,
  convertUTCToTimeZoneDateString,
  convertUTCToTimeZoneDateTimeString,
} from "../Utility/DateTimeUtility";

const WorkItemDetails = forwardRef(
  (
    {
      serviceId,
      onEditPrescription,
      onAddNewPrescription,
      onAddNote,
      onViewNote,
      onAddMedicalRecord,
    },
    ref
  ) => {
    const { showSnackbar } = useSnackbar();
    const { auth } = useContext(AuthContext);
    const [service, setService] = useState({});
    const [prescriptions, setPrescriptions] = useState([]);
    const [clinicalNotes, setClinicalNotes] = useState([]);
    const [medicalRecords, setMedicalRecords] = useState([]);
    const [showAddPrescriptionrMenu, setShowAddPrescriptionrMenu] =
      useState(false);
    const [addPrescriptionMenuAnchor, setAddPrescriptionMenuAnchor] =
      useState(null);
    const [showAddNoteMenu, setShowAddNoteMenu] = useState(false);
    const [addNoteMenuAnchor, setAddNoteMenuAnchor] = useState(null);
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [isLoadingMedicalRecords, setIsLoadingMedicalRecords] =
      useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [isLoadingServiceData, setIsLoadingServiceData] = useState(false);
    const [isLoadingPrescriptions, setIsLoadingPrescriptions] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [isLoadingClinicalNotes, setIsLoadingClinicalNotes] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const renderPatientDetailsCard = () => {
      return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
          <CardContent sx={{ padding: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Patient's Details
            </Typography>

            <Divider sx={{ my: 1 }} />

            {/* Patient details section - now takes full width */}
            <Box sx={{ width: "100%" }}>
              <Box sx={{ "& > div": { mb: 1 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Name:</Typography>
                  <Typography variant="body2">
                    <Link to="/patient" state={service?.patientId}>
                      {service?.patientFirstName}&nbsp;
                      {service?.patientLastName}
                    </Link>
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">MRN:</Typography>
                  <Typography variant="body2" align="right">
                    {service.patientMRN || "-"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Age:</Typography>
                  <Typography variant="body2">
                    {formatAge(service.patientAgeInYears)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Gender:</Typography>
                  <Typography variant="body2">
                    {service.patientGender || "-"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Contact:</Typography>
                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "break-word" }}
                  >
                    {service.patientMobileNo || "-"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Email:</Typography>
                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "break-word" }}
                    align="right"
                  >
                    {service.patientEmailId || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Photo section - moved below the details */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <img
                src={
                  service?.patient?.photo
                    ? service?.patient?.photo
                    : service?.patient?.gender === "Male"
                    ? "./placeholder-image-male-gender.webp"
                    : "./placeholder-image-female-gender.webp"
                }
                className="border rounded p-0"
                alt="PatientPhoto"
                width="80"
                height="80"
              />
            </Box>
          </CardContent>
        </Card>
      );
    };

    // Render visit details card for mobile view
    const renderVisitDetailsCard = () => {
      return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
          <CardContent sx={{ padding: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Visit Details
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ "& > div": { mb: 1 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Status:</Typography>
                <Typography variant="body2">
                  {service.visitStatus || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Booking Type:</Typography>
                <Typography variant="body2">Walk In</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ width: 150 }}>
                  Scheduled At:
                </Typography>
                <Typography variant="body2" align="right">
                  {convertUTCToTimeZoneDateTimeString(
                    service?.scheduledStartDateTime,
                    auth?.slectedFacility?.timeZone
                  )}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Booked By:</Typography>
                <Typography variant="body2">
                  {service.createdByUserName || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ width: 150 }}>
                  Time of Booking:
                </Typography>
                <Typography variant="body2" align="right">
                  {convertUTCToTimeZoneDateTimeString(
                    service?.createdOn,
                    auth?.slectedFacility?.timeZone
                  )}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    };

    // Render primary service card for mobile view
    const renderPrimaryServiceCard = () => {
      return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
          <CardContent sx={{ padding: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Primary Service Scheduled With
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ "& > div": { mb: 1 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Name:</Typography>
                <Typography>{service?.assignedToUserName}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">ID:</Typography>
                <Typography variant="body2">
                  {service.physicianProfessionalId || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Mobile No.:</Typography>
                <Typography>{service.physicianMobileNo}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Email Id:</Typography>
                <Typography>{service.physicianEmailId}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    };

    const renderPrescriptionCards = () => (
      <Box sx={{ px: 1, maxHeight: "350px", overflowY: "auto" }}>
        {prescriptions.map((prescription) => (
          <Card
            key={prescription.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ flexGrow: 1 }}
                >
                  {prescription.displayId}
                </Typography>
                <Chip
                  label={
                    prescription?.status ? prescription?.status : "Not Assigned"
                  }
                  color={
                    prescription.status === "Draft"
                      ? "default"
                      : prescription.status === "Completed"
                      ? "success"
                      : "primary"
                  }
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created By:</strong> {prescription.physicianName}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Type:</strong> {prescription.prescriptionType}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created On:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  prescription?.createdOn,
                  auth?.slectedFacility?.timeZone
                )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Completion Time:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  prescription?.lastModifiedOn,
                  auth?.selectedFacility?.timeZone
                )}
              </Typography>
            </CardContent>

            <CardActions
              sx={{
                justifyContent: "flex-end",
                px: 2,
                pb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {prescription?.status === "Draft" ? (
                <>
                  <Tooltip title="Edit Prescription" arrow>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        onEditPrescription(service?.id, prescription)
                      }
                    >
                      <CreateOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove Prescription" arrow>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => onRemovePrescriptionClick(prescription)}
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="View Prescription" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      onEditPrescription(service?.id, prescription)
                    }
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </CardActions>
          </Card>
        ))}

        {prescriptions.length === 0 && !isLoadingPrescriptions && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No prescriptions found
            </Typography>
          </Box>
        )}

        {isLoadingPrescriptions && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    );

    const renderClinicalNotesCards = () => (
      <Box sx={{ px: 1, maxHeight: "350px", overflowY: "auto" }}>
        {clinicalNotes.map((note) => (
          <Card
            key={note.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ flexGrow: 1 }}
                >
                  {note.displayId}
                </Typography>
                <Chip label={note.noteType} color="primary" size="small" />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created By:</strong> {note.createdByUserName}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created On:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  note?.createdOn,
                  auth?.slectedFacility?.timeZone
                )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Last Modified On:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  note?.lastModifiedOn,
                  auth?.slectedFacility?.timeZone
                )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Last Modified By:</strong>{" "}
                {note?.lastModifiedByUserName || ""}
              </Typography>
            </CardContent>

            <CardActions
              sx={{
                justifyContent: "flex-end",
                px: 2,
                pb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Tooltip title="View Note" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onViewNote(service?.id, note)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove Note" arrow>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onRemoveNoteClick(note)}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </CardActions>
          </Card>
        ))}

        {clinicalNotes.length === 0 && !isLoadingNotes && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No Rows
            </Typography>
          </Box>
        )}

        {isLoadingNotes && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    );

    const renderMedicalRecordsCards = () => (
      <Box sx={{ px: 1, maxHeight: "350px", overflowY: "auto" }}>
        {medicalRecords.map((record) => (
          <Card
            key={record.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ flexGrow: 1 }}
                >
                  {record.name}
                </Typography>
                <Chip
                  label={capitalizeFirstLetter(record?.recordType || "")}
                  color="primary"
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Record Date:</strong>{" "}
                {convertUTCToTimeZoneDateString(
                  record?.recordDateTime,
                  auth?.slectedFacility?.timeZone
                )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Uploaded Date Time:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  record?.dateOfUpload,
                  auth?.slectedFacility?.timeZone
                )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Uploaded By:</strong> {record.createdByUserName}
              </Typography>
            </CardContent>

            <CardActions
              sx={{
                justifyContent: "flex-end",
                px: 2,
                pb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Tooltip title="Preview Medical Record" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePreviewMedicalRecord(record);
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </CardActions>
          </Card>
        ))}

        {medicalRecords.length === 0 && !isLoadingMedicalRecords && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No Rows
            </Typography>
          </Box>
        )}

        {isLoadingMedicalRecords && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    );

    const prescriptionGridColumns = [
      { field: "displayId", headerName: "ID", width: 200 },
      { field: "physicianName", headerName: "Created By", width: 200 },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) =>
          params?.row?.status ? params?.row?.status : "Not Assigned",
      },
      {
        field: "prescriptionType",
        headerName: "Type",
        width: 100,
      },
      {
        field: "createdOn",
        headerName: "Created On",
        width: 250,
        renderCell: (params) =>
          convertUTCToTimeZoneDateTimeString(
            params?.row?.createdOn,
            auth?.slectedFacility?.timeZone
          ),
      },
      {
        field: "lastModifiedOn",
        headerName: "Completion Time",
        width: 250,
        renderCell: (params) =>
          convertUTCToTimeZoneDateTimeString(
            params?.row?.lastModifiedOn,
            auth?.selectedFacility?.timeZone
          ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        headerClassName: "sticky-column",
        cellClassName: "sticky-column",
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            {params?.row?.status === "Draft" ? (
              <>
                <Tooltip title="Edit Prescription" arrow>
                  <IconButton
                    variant="outlined"
                    onClick={() => onEditPrescription(service?.id, params.row)}
                  >
                    <CreateOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Prescription" arrow>
                  <IconButton
                    variant="outlined"
                    onClick={() => onRemovePrescriptionClick(params.row)}
                  >
                    <CloseOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="View Prescription" arrow>
                <IconButton
                  variant="outlined"
                  onClick={() => onEditPrescription(service?.id, params.row)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];

    const notesGridColumns = [
      { field: "displayId", headerName: "ID", width: 200 },
      { field: "createdByUserName", headerName: "Created By", width: 200 },
      {
        field: "noteType",
        headerName: "Type",
        width: 100,
      },
      {
        field: "createdOn",
        headerName: "createdOn",
        width: 200,
        renderCell: (params) =>
          convertUTCToTimeZoneDateTimeString(
            params?.row?.createdOn,
            auth?.slectedFacility?.timeZone
          ),
      },
      {
        field: "lastModifiedOn",
        headerName: "Last Modified On",
        width: 200,
        renderCell: (params) =>
          convertUTCToTimeZoneDateTimeString(
            params?.row?.lastModifiedOn,
            auth?.slectedFacility?.timeZone
          ),
      },
      {
        field: "lastModifiedByUserName",
        headerName: "Last Modified By",
        width: 200,
        renderCell: (params) => params?.row?.lastModifiedByUserName || "",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        headerClassName: "sticky-column",
        cellClassName: "sticky-column",
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="View Note" arrow>
              <IconButton
                variant="outlined"
                onClick={() => onViewNote(service?.id, params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove Note" arrow>
              <IconButton
                variant="outlined"
                onClick={() => onRemoveNoteClick(params.row)}
              >
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];

    const medicalRecordsGridColumns = [
      { field: "name", headerName: "File Name", width: 200 },
      {
        field: "recordType",
        headerName: "Record Type",
        width: 150,
        renderCell: (params) =>
          capitalizeFirstLetter(params?.row?.recordType || ""),
      },
      {
        field: "recordDateTime",
        headerName: "Record Date",
        width: 150,
        renderCell: (params) =>
          convertUTCToTimeZoneDateString(
            params?.row?.recordDateTime,
            auth?.slectedFacility?.timeZone
          ),
      },
      {
        field: "dateOfUpload",
        headerName: "Uploaded Date Time",
        width: 200,
        renderCell: (params) =>
          convertUTCToTimeZoneDateTimeString(
            params?.row?.dateOfUpload,
            auth?.slectedFacility?.timeZone
          ),
      },
      {
        field: "createdByUserName",
        headerName: "Uploaded By",
        width: 150,
      },

      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        headerClassName: "sticky-column",
        cellClassName: "sticky-column",
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Preview Medical Record" arrow>
              <IconButton
                variant="outlined"
                onClick={(event) => {
                  // Stop event propagation to prevent the row click handler from firing
                  event.stopPropagation();
                  // Then call your handler
                  handlePreviewMedicalRecord(params.row);
                }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];
    useEffect(() => {
      const getService = async () => {
        setIsLoadingServiceData(true);
        const result = await getServiceDetails(auth, serviceId);
        if (result?.isSuccess) {
          setIsLoadingServiceData(false);
          setService(result?.data);
        } else {
          setIsLoadingServiceData(false);
          showSnackbar("Failed to load service details", "error");
        }
      };

      const loadPrescriptions = async () => {
        if (auth != null && serviceId != null) {
          setIsLoadingPrescriptions(true);
          var result = await getPrescriptionsForService(auth, serviceId);
          if (result?.isSuccess) {
            setPrescriptions(result.data);
          } else {
            showSnackbar("Failed to load prescriptions", "error");
            //setIsLoadingPrescriptions(false);
          }
          setIsLoadingPrescriptions(false);
        }
      };
      const loadClinicalNotes = async () => {
        if (auth != null && serviceId != null) {
          setIsLoadingNotes(true);
          var result = await getClinicalNotesForService(auth, serviceId);
          if (result?.isSuccess) {
            setClinicalNotes(result.data);
          } else {
            showSnackbar("Failed to load clinical notes", "error");
          }
          setIsLoadingNotes(false);
        }
      };

      const loadMedicalRecords = async () => {
        if (auth != null && serviceId != null) {
          setIsLoadingMedicalRecords(true);
          var result = await getMedicalRecordMetadata(auth, serviceId);
          if (result?.isSuccess) {
            setMedicalRecords(result.data);
          } else {
            showSnackbar("Failed to load medical records", "error");
          }
          setIsLoadingMedicalRecords(false);
        }
      };

      getService();
      loadPrescriptions();
      loadClinicalNotes();
      loadMedicalRecords();
    }, [auth, serviceId]);

    const onRemovePrescriptionClick = async (prescription) => {
      if (auth != null && prescription != null) {
        var result = await removePrescription(auth, prescription?.id);
        if (result?.isSuccess) {
          refreshPrescriptions();
        }
      }
    };

    const onRemoveNoteClick = async (note) => {
      if (auth != null && note != null) {
        try {
          var result = await deleteClinicalNote(auth, note?.id);
          if (result?.isSuccess) {
            refreshClinicalNotes();
          }
        } catch (error) {
          console.error("Error deleting clinical note:", error);
        }
      }
    };

    const handlePreviewMedicalRecord = async (record) => {
      if (auth != null && record != null) {
        setSelectedRecord(record);
        setPreviewDialogOpen(true);
        setPreviewLoading(true);

        try {
          const result = await downloadMedicalRecord(auth, record.id);

          if (result?.isSuccess && result.data) {
            // Create a blob from the response data WITHOUT auto-downloading
            // const blob = new Blob([result.data]);
            // const url = window.URL.createObjectURL(blob);
            // window.open(url, "_blank"); // Open in a new tab for preview
            const fileBlob = new Blob([result.data], {
              type: result.contentType, // or whatever type you expect
            });

            // Create blob URL for preview only
            const blobUrl = URL.createObjectURL(fileBlob);
            window.open(blobUrl, result.fileName); // Open in a new tab for preview
            setSelectedRecord({
              ...record,
              fileBlob: fileBlob, // Store the blob directly
              blobUrl: blobUrl,
              fileName: result.fileName || record.name,
            });
          } else {
            console.error("Failed to preview medical record");
          }
        } catch (error) {
          console.error("Error previewing medical record:", error);
        } finally {
          setPreviewLoading(false);
        }
      }
    };

    const refreshPrescriptions = async () => {
      if (auth != null && serviceId != null) {
        try {
          setIsLoadingPrescriptions(true);
          var result = await getPrescriptionsForService(auth, serviceId);
          if (result?.isSuccess) {
            setPrescriptions(result.data);
          }
        } catch (error) {
          console.error("Error refreshing prescriptions:", error);
        } finally {
          setIsLoadingPrescriptions(false);
        }
      }
    };

    const refreshClinicalNotes = async () => {
      if (auth != null && serviceId != null) {
        try {
          setIsLoadingNotes(true);
          var result = await getClinicalNotesForService(auth, serviceId);
          if (result?.isSuccess) {
            setClinicalNotes(result.data);
          }
        } catch (error) {
          console.error("Error loading clinical notes:", error);
        } finally {
          setIsLoadingNotes(false);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      refreshPrescriptions,
      refreshClinicalNotes,
    }));

    return (
      <Grid2
        container
        spacing={2}
        sx={{ paddingX: "5px", paddingTop: "5px", paddingBottom: "50px",
            overflow: "auto", // Enable scrolling when content overflows
            maxHeight: "80vh", // Adjust as needed for your layout
        }}
      >
        {isLoadingServiceData && <LoadingScreen />}
        <Grid2 container size={12}></Grid2>
        <Grid2 container size={12} sx={{ paddingX: "5px" }}>
          {isMobile ? (
            // Mobile view layout using cards
            <Grid2 container size={12} sx={{ px: 1 }}>
              {renderPatientDetailsCard()}
              {renderVisitDetailsCard()}
              {renderPrimaryServiceCard()}
            </Grid2>
          ) : (
            <Grid2 container size={12} sx={{ paddingX: "5px" }}>
              {/* Patient Details Card */}
              <Grid2 container size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                <Card variant="outlined" sx={{ width: "100%" }}>
                  <CardHeader subheader="Patient's Details"></CardHeader>
                  <CardContent>
                    <Grid2 container size={12}>
                      <Grid2 size={9}>
                        <Grid2 container size={12}>
                          <Grid2 size={3}>
                            <Typography>Name</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Link to="/patient" state={service?.patientId}>
                              {service?.patientFirstName}&nbsp;
                              {service?.patientLastName}
                            </Link>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={3}>
                            <Typography>MRN</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Typography>{service?.patientMRN}&nbsp;</Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={3}>
                            <Typography>Age</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Typography>
                              {formatAge(service?.patientAgeInYears)}
                            </Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={3}>
                            <Typography>Gender</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Typography>{service?.patientGender}</Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={3}>
                            <Typography>Contact</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Typography sx={{ overflowWrap: "break-word" }}>
                              {service?.patientMobileNo}
                            </Typography>
                          </Grid2>
                          <Grid2 size={3}>
                            <Typography>Email</Typography>
                          </Grid2>
                          <Grid2 size={9}>
                            <Typography sx={{ overflowWrap: "break-word" }}>
                              {service?.patientEmailId}
                            </Typography>
                          </Grid2>
                        </Grid2>
                      </Grid2>
                      <Grid2
                        container
                        size={3}
                        sx={{
                          overflow: "hidden",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={
                            service?.patient?.photo
                              ? service?.patient?.photo
                              : service?.patientGender === "Male"
                              ? "./placeholder-image-male-gender.webp"
                              : "./placeholder-image-female-gender.webp"
                          }
                          className="border rounded p-0"
                          alt="PatientPhoto"
                          width="120"
                          height="120"
                        />
                      </Grid2>
                    </Grid2>
                  </CardContent>
                </Card>
              </Grid2>

              {/* Visit Details Card */}
              <Grid2 container size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                <Grid2 size={12}>
                  <Card
                    variant="outlined"
                    sx={{ width: "100%", height: "100%" }}
                  >
                    <CardHeader subheader="Visit Details"></CardHeader>
                    <CardContent>
                      <Grid2 container size={12}>
                        <Grid2 size={4}>
                          <Typography>Status</Typography>
                        </Grid2>
                        <Grid2 size={8}>
                          <Typography>{service.visitStatus}</Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={4}>
                          <Typography>Booking Type</Typography>
                        </Grid2>
                        <Grid2 size={8}>
                          <Typography>Walk In</Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={4}>
                          <Typography>Scheduled At</Typography>
                        </Grid2>
                        <Grid2 size={8}>
                          <Typography>
                            {convertUTCToTimeZoneDateTimeString(
                              service?.scheduledStartDateTime,
                              auth?.slectedFacility?.timeZone
                            )}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={4}>
                          <Typography>Booked By</Typography>
                        </Grid2>
                        <Grid2 size={8}>
                          <Typography>{service.createdByUserName}</Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={4}>
                          <Typography>Time of Booking</Typography>
                        </Grid2>
                        <Grid2 size={8}>
                          <Typography>
                            {convertUTCToTimeZoneDateTimeString(
                              service?.createdOn,
                              auth?.slectedFacility?.timeZone
                            )}
                            {/* {convertUTCToTimeZone(service?.createdOn, auth?.slectedFacility?.timeZone)} */}
                          </Typography>
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
              </Grid2>

              {/* Physician Details Card */}
              <Grid2 container size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                <Card variant="outlined" sx={{ width: "100%" }}>
                  <CardHeader subheader="Primary Service Scheduled With"></CardHeader>
                  <CardContent>
                    <Grid2 container size={12}>
                      <Grid2 size={3}>
                        <Typography>Name</Typography>
                      </Grid2>
                      <Grid2 size={9}>
                        <Typography>{service?.assignedToUserName}</Typography>
                      </Grid2>
                    </Grid2>
                    {/* <Grid2 container size={12}>
                  <Grid2 size={3}>
                    <Typography>ID</Typography>
                  </Grid2>
                  <Grid2 size={9}>
                    <Typography>{service.assignedToId}&nbsp;</Typography>
                  </Grid2>
                </Grid2> */}
                    <Grid2 container size={12}>
                      <Grid2 size={3}>
                        <Typography>Mobile No.</Typography>
                      </Grid2>
                      <Grid2 size={9}>
                        <Typography>{service.physicianMobileNo}</Typography>
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12}>
                      <Grid2 size={3}>
                        <Typography>Email Id</Typography>
                      </Grid2>
                      <Grid2 size={9}>
                        <Typography>{service.physicianEmailId}</Typography>
                      </Grid2>
                    </Grid2>
                  </CardContent>
                </Card>
              </Grid2>
            </Grid2>
          )}

          {/* Prescriptions Accordion */}
          <Accordion
            variant="outlined"
            sx={{ width: "100%", maxWidth: "100%" }}
          >
            <AccordionSummary
              sx={{
                minHeight: "40px",
                maxHeight: "40px",
                "&.Mui-expanded": {
                  minHeight: "30px",
                  maxHeight: "30px",
                },
              }}
              expandIcon={<GridExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Typography component="span">Prescriptions</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
              <Grid2 container size={12} sx={{ height: "400px" }}>
                <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
                  <CardHeader
                    sx={{ paddingY: "10px" }}
                    action={
                      <Tooltip
                        title="Add new prescription"
                        arrow
                        placement="top"
                      >
                        <IconButton
                          color="secondary"
                          onClick={(e) => {
                            setShowAddPrescriptionrMenu(true);
                            setAddPrescriptionMenuAnchor(e.currentTarget);
                          }}
                          sx={iconButtonStyles.circularSecondary}
                        >
                          <AddRounded fontSize="small" />
                        </IconButton>
                        <Menu
                          id="menu-appbar"
                          anchorEl={addPrescriptionMenuAnchor}
                          keepMounted
                          open={showAddPrescriptionrMenu}
                          onClose={() => setShowAddPrescriptionrMenu(false)}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              setShowAddPrescriptionrMenu(false);
                              setAddPrescriptionMenuAnchor(null);
                              onAddNewPrescription(serviceId, PrescriptionTypes.GeneralPrescription);
                            }}
                          >
                            General Prescription
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setShowAddPrescriptionrMenu(false);
                              setAddPrescriptionMenuAnchor(null);
                              onAddNewPrescription(serviceId, PrescriptionTypes.PhysiotherapyPrescription);
                            }}
                          >
                            Physiotherapy Prescription
                          </MenuItem>
                        </Menu>
                      </Tooltip>
                    }
                  ></CardHeader>
                  <CardContent sx={{ border: "1px", height: "90%" }}>
                    {isMobile ? (
                      renderPrescriptionCards()
                    ) : (
                      <DataGrid
                        sx={{
                          height: "100%",
                          "& .sticky-column": {
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 100,
                          },
                        }}
                        initialState={{ density: "compact" }}
                        columns={prescriptionGridColumns}
                        hideFooter={true}
                        rows={prescriptions}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        disableColumnSorting={true}
                        loading={isLoadingPrescriptions}
                        localeText={{ noRowsLabel: "No prescriptions found" }}
                        slotProps={{
                          loadingOverlay: {
                            variant: "skeleton",
                            noRowsVariant: "skeleton",
                          },
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid2>
            </AccordionDetails>
          </Accordion>

          {/* Clinical Notes Accordion */}
          <Accordion
            variant="outlined"
            sx={{ width: "100%", maxWidth: "100%" }}
          >
            <AccordionSummary
              sx={{
                minHeight: "40px",
                maxHeight: "40px",
                "&.Mui-expanded": {
                  minHeight: "30px",
                  maxHeight: "30px",
                },
              }}
              expandIcon={<GridExpandMoreIcon />}
              aria-controls="panel3-content"
              id="panel3-header"
            >
              <Typography component="span">Clinical Notes</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
              <Grid2 container size={12} sx={{ height: "400px" }}>
                <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
                  <CardHeader
                    sx={{ paddingY: "10px" }}
                    action={
                      <Tooltip
                        title="Add new clinical note"
                        arrow
                        placement="top"
                      >
                        <IconButton
                          color="secondary"
                          onClick={(e) => {
                            setShowAddNoteMenu(true);
                            setAddNoteMenuAnchor(e.currentTarget);
                          }}
                          sx={iconButtonStyles.circularSecondary}
                        >
                          <AddRounded fontSize="small" />
                        </IconButton>
                        <Menu
                          id="clinical-note-menu"
                          anchorEl={addNoteMenuAnchor}
                          keepMounted
                          open={showAddNoteMenu}
                          onClose={() => setShowAddNoteMenu(false)}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              setShowAddNoteMenu(false);
                              setAddNoteMenuAnchor(null);
                              onAddNote(serviceId);
                            }}
                          >
                            Notes
                          </MenuItem>
                        </Menu>
                      </Tooltip>
                    }
                  ></CardHeader>
                  <CardContent sx={{ border: "1px", height: "90%" }}>
                    {isMobile ? (
                      renderClinicalNotesCards()
                    ) : (
                      <DataGrid
                        sx={{
                          height: "100%",
                          "& .sticky-column": {
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 100,
                          },
                        }}
                        initialState={{ density: "compact" }}
                        columns={notesGridColumns}
                        hideFooter={true}
                        rows={clinicalNotes}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        disableColumnSorting={true}
                        loading={isLoadingNotes}
                        localeText={{ noRowsLabel: "No Rows" }}
                        slotProps={{
                          loadingOverlay: {
                            variant: "skeleton",
                            noRowsVariant: "skeleton",
                          },
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid2>
            </AccordionDetails>
          </Accordion>

          {/* Medical Records Accordion */}
          <Accordion
            variant="outlined"
            sx={{ width: "100%", maxWidth: "100%" }}
          >
            <AccordionSummary
              sx={{
                minHeight: "40px",
                maxHeight: "40px",
                "&.Mui-expanded": {
                  minHeight: "30px",
                  maxHeight: "30px",
                },
              }}
              expandIcon={<GridExpandMoreIcon />}
              aria-controls="panel4-content"
              id="panel4-header"
            >
              <Typography component="span">Medical Records</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
              <Grid2 container size={12} sx={{ height: "400px" }}>
                <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
                  <CardHeader
                    sx={{ paddingY: "10px" }}
                    action={
                      <Tooltip
                        title="Add new medical record"
                        arrow
                        placement="top"
                      >
                        <IconButton
                          color="secondary"
                          onClick={() => onAddMedicalRecord(serviceId)}
                          sx={iconButtonStyles.circularSecondary}
                        >
                          <AddRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  ></CardHeader>
                  <CardContent sx={{ border: "1px", height: "90%" }}>
                    {isMobile ? (
                      renderMedicalRecordsCards()
                    ) : (
                      <DataGrid
                        sx={{
                          height: "100%",
                          "& .sticky-column": {
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 100,
                          },
                        }}
                        initialState={{ density: "compact" }}
                        columns={medicalRecordsGridColumns}
                        hideFooter={true}
                        rows={medicalRecords}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        disableColumnSorting={true}
                        loading={isLoadingMedicalRecords}
                        localeText={{ noRowsLabel: "No Rows" }}
                        slotProps={{
                          loadingOverlay: {
                            variant: "skeleton",
                            noRowsVariant: "skeleton",
                          },
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>
      </Grid2>
    );
  }
);

export default WorkItemDetails;
